/**
 * Enhanced Cart Drawer JavaScript
 * Handles interactive features for the redesigned cart drawer
 */

class EnhancedCartDrawer {
  constructor() {
    this.init();
  }

  init() {
    this.setupFreeMinisCarousel();
    this.setupClaimButtons();
    this.setupRecommendationATC();
    this.updateProgressBar();
    this.watchCartChanges();
  }

  /**
   * Watch for cart total changes by intercepting fetch calls.
   * MutationObserver approaches fail on mobile because:
   *   - The cart drawer may not be in the DOM when the observer is set up.
   *   - DOM replacement (innerHTML swap) detaches the observed node silently.
   * Intercepting window.fetch is browser-agnostic and catches every cart
   * mutation regardless of which component triggers it.
   */
  watchCartChanges() {
    const self = this;
    const originalFetch = window.fetch;

    window.fetch = async function (...args) {
      const response = await originalFetch.apply(this, args);

      const url = (typeof args[0] === 'string' ? args[0] : args[0]?.url) || '';
      const isCartMutation =
        url.includes('/cart/change') ||
        url.includes('/cart/add') ||
        url.includes('/cart/remove');

      if (response.ok && isCartMutation) {
        // /cart/change.js and /cart/add.js responses contain total_price directly.
        // Clone before reading so the original response body stays intact for the caller.
        response.clone().json().then(data => {
          if (typeof data.total_price === 'number') {
            self.updateProgressBarWithTotal(data.total_price);
          } else {
            self.fetchAndUpdateProgressBar();
          }
        }).catch(() => self.fetchAndUpdateProgressBar());
      }

      return response;
    };

    // Initial update when drawer opens
    setTimeout(() => this.fetchAndUpdateProgressBar(), 300);
  }

  /**
   * Fetch live cart total from Shopify and update the progress bar
   */
  async fetchAndUpdateProgressBar() {
    try {
      const cart = await fetch('/cart.js').then(r => r.json());
      this.updateProgressBarWithTotal(cart.total_price);
    } catch (e) {
      // Fallback: read from DOM
      this.updateProgressBar();
    }
  }

  /**
   * Quantity Controls
   */
  setupQuantityControls() {
    // Remove any existing event listeners by cloning and replacing
    const plusButtons = document.querySelectorAll('.quantity__button[name="plus"]');
    const minusButtons = document.querySelectorAll('.quantity__button[name="minus"]');
    
    // Plus buttons - use capture phase to intercept first
    plusButtons.forEach(btn => {
      const newBtn = btn.cloneNode(true);
      btn.parentNode.replaceChild(newBtn, btn);
      
      newBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        
        const input = newBtn.parentElement.querySelector('.quantity__input');
        if (!input) return;
        
        const line = parseInt(input.dataset.index);
        const currentQty = parseInt(input.value);
        const max = input.max ? parseInt(input.max) : 999;
        
        console.log('Plus clicked - Line:', line, 'Current:', currentQty);
        
        if (currentQty < max) {
          this.updateQuantity(line, currentQty + 1);
        }
      }, true);
    });

    // Minus buttons
    minusButtons.forEach(btn => {
      const newBtn = btn.cloneNode(true);
      btn.parentNode.replaceChild(newBtn, btn);
      
      newBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        
        const input = newBtn.parentElement.querySelector('.quantity__input');
        if (!input) return;
        
        const line = parseInt(input.dataset.index);
        const currentQty = parseInt(input.value);
        const min = input.min ? parseInt(input.min) : 0;
        
        console.log('Minus clicked - Line:', line, 'Current:', currentQty);
        
        if (currentQty > min) {
          this.updateQuantity(line, currentQty - 1);
        }
      }, true);
    });

    // Direct input change
    document.querySelectorAll('.quantity__input').forEach(input => {
      input.addEventListener('change', (e) => {
        const line = parseInt(e.target.dataset.index);
        const newQty = parseInt(e.target.value);
        console.log('Input changed - Line:', line, 'New qty:', newQty);
        if (newQty >= 0) {
          this.updateQuantity(line, newQty);
        }
      });
    });
  }

  /**
   * Update cart quantity via AJAX
   */
  async updateQuantity(line, quantity) {
    console.log('updateQuantity called - Line:', line, 'Quantity:', quantity);
    
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          line: line,
          quantity: quantity
        })
      });

      console.log('Cart API response status:', response.status);

      if (response.ok) {
        const cart = await response.json();
        console.log('Cart updated, new total:', cart.total_price);
        
        // Update UI elements immediately without refresh
        this.updateCartUIOnly(cart, line, quantity);
      } else {
        console.error('Cart update failed:', response.statusText);
      }
    } catch (error) {
      console.error('Error updating quantity:', error);
    }
  }

  /**
   * Update only the UI elements without full page refresh
   */
  updateCartUIOnly(cart, line, quantity) {
    // Update the specific item's quantity input
    const qtyInput = document.querySelector(`.quantity__input[data-index="${line}"]`);
    if (qtyInput) {
      qtyInput.value = quantity;
    }

    // Find the cart item for this line
    const cartItem = cart.items.find((item, index) => (index + 1) === line);
    
    if (cartItem && quantity > 0) {
      // Update item line price
      const itemElement = document.querySelector(`[data-cart-line="${line}"]`);
      if (itemElement) {
        const priceElement = itemElement.querySelector('.cart-item__final-price, .product-price');
        if (priceElement) {
          priceElement.textContent = this.formatMoney(cartItem.final_line_price);
        }
      }
    }

    // Update subtotal display
    const subtotalElement = document.querySelector('.subtotal-final');
    if (subtotalElement) {
      subtotalElement.textContent = this.formatMoney(cart.total_price);
    }

    // Update item count in header/badge
    const itemCountElements = document.querySelectorAll('.cart-count-bubble span, [data-cart-count]');
    itemCountElements.forEach(el => {
      el.textContent = cart.item_count;
    });

    // Update progress bar immediately - THIS IS THE KEY PART
    console.log('Updating progress bar with total:', cart.total_price);
    this.updateProgressBarWithTotal(cart.total_price);

    // If quantity is 0, remove the item from DOM
    if (quantity === 0) {
      const itemElement = document.querySelector(`[data-cart-line="${line}"]`);
      if (itemElement) {
        itemElement.style.animation = 'fadeOut 0.3s ease';
        setTimeout(() => {
          itemElement.remove();
          // Check if cart is empty
          if (cart.item_count === 0) {
            this.showEmptyCart();
          }
        }, 300);
      }
    }
  }

  /**
   * Show empty cart message
   */
  showEmptyCart() {
    const cartItems = document.querySelector('.cart-items');
    if (cartItems) {
      cartItems.innerHTML = '<p style="text-align: center; padding: 20px;">Your cart is empty</p>';
    }
  }

  /**
   * Format money in Indian Rupees
   */
  formatMoney(cents) {
    const rupees = (cents / 100).toFixed(2);
    return `₹${rupees}`;
  }

  /**
   * Update progress bar with specific cart total (2-milestone layout)
   */
  updateProgressBarWithTotal(cartTotal) {
    const progressWrapper = document.querySelector('.cart-progress-bar-wrapper');
    if (!progressWrapper) return;

    // NOTE: data-milestone-1 → dataset['milestone-1'] (hyphen+digit is NOT camelCased)
    const milestone1 = parseInt(progressWrapper.dataset['milestone-1']) || 49900;

    let progressPercent = 0;
    let milestoneReached = 0;

    if (cartTotal >= milestone1) {
      progressPercent = 100;
      milestoneReached = 1;
    } else {
      progressPercent = (cartTotal / milestone1) * 100;
    }

    // Update progress fill
    const progressFill = document.querySelector('.progress-fill');
    if (progressFill) progressFill.style.width = `${progressPercent}%`;

    // Update milestone dots
    document.querySelectorAll('.milestone-dot').forEach((dot, index) => {
      if (index < milestoneReached) {
        dot.classList.add('reached');
      } else {
        dot.classList.remove('reached');
      }
    });

    // Update reward labels
    document.querySelectorAll('.reward-label').forEach((label, index) => {
      if (index < milestoneReached) {
        label.classList.add('reached');
      } else {
        label.classList.remove('reached');
      }
    });

    // Update price labels
    document.querySelectorAll('.price-label').forEach((label, index) => {
      if (index < milestoneReached) {
        label.classList.add('reached');
      } else {
        label.classList.remove('reached');
      }
    });

    // Update data attribute
    progressWrapper.dataset.cartTotal = cartTotal;

    // Update the dynamic status text
    this.updateProgressText(cartTotal, progressWrapper, milestone1);
  }

  /**
   * Update the two-line status text below the progress bar
   */
  updateProgressText(cartTotal, wrapper, milestone1) {
    const statusEl = document.getElementById('cart-progress-status');
    if (!statusEl) return;

    const reward1 = wrapper.dataset.reward1 || 'FREE SHIPPING';

    let html = '';

    if (cartTotal >= milestone1) {
      html += `<p class="progress-status-congrats">CONGRATS! YOU UNLOCKED ${reward1}! 🎉</p>`;
    } else {
      const amountToNext = Math.ceil((milestone1 - cartTotal) / 100);
      html += `<p class="progress-status-next">Shop for <strong>₹${amountToNext}</strong> more to unlock ${reward1}</p>`;
    }

    statusEl.innerHTML = html;
  }

  /**
   * Add to Cart buttons on recommendation cards — use delegation so it
   * works even after the cart drawer re-renders
   */
  setupRecommendationATC() {
    document.addEventListener('click', async (e) => {
      const btn = e.target.closest('.recommendation-atc');
      if (!btn || btn.disabled) return;
      e.preventDefault();
      const variantId = btn.dataset.variantId;
      if (!variantId) return;
      await this.addToCart(variantId, btn);
    });
  }

  async addToCart(variantId, button) {
    const originalText = button.textContent;
    button.disabled = true;
    button.textContent = '...';

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: parseInt(variantId), quantity: 1 })
      });

      if (response.ok) {
        button.textContent = 'ADDED ✓';
        button.classList.add('added');

        // Re-render cart items so the new product appears immediately
        await this.refreshCartSection();

        setTimeout(() => {
          if (button.isConnected) {
            button.textContent = originalText;
            button.classList.remove('added');
            button.disabled = false;
          }
        }, 2000);
      } else {
        throw new Error('Add to cart failed');
      }
    } catch (err) {
      button.textContent = originalText;
      button.disabled = false;
    }
  }

  /**
   * Re-render cart items & footer using Shopify Sections API,
   * then update the progress bar and cart count badge.
   */
  async refreshCartSection() {
    try {
      const [sectionsResponse, cart] = await Promise.all([
        fetch('/?sections=cart-drawer'),
        fetch('/cart.js').then(r => r.json())
      ]);

      const data = await sectionsResponse.json();
      const sectionHTML = data['cart-drawer'];

      if (sectionHTML) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(sectionHTML, 'text/html');

        // Replace cart items list
        const newItems = doc.querySelector('#CartDrawer-CartItems');
        const currentItems = document.querySelector('#CartDrawer-CartItems');
        if (newItems && currentItems) {
          currentItems.innerHTML = newItems.innerHTML;
        }

        // Replace subtotal
        const newSubtotal = doc.querySelector('.subtotal-final');
        const currentSubtotal = document.querySelector('.subtotal-final');
        if (newSubtotal && currentSubtotal) {
          currentSubtotal.textContent = newSubtotal.textContent;
        }
      }

      // Update cart count badges
      document.querySelectorAll('.cart-count-bubble span, [data-cart-count], .cart-count').forEach(el => {
        el.textContent = cart.item_count;
      });

      // Update progress bar
      this.updateProgressBarWithTotal(cart.total_price);

      // Re-init carousel for the refreshed items list
      this.setupFreeMinisCarousel();

    } catch (err) {
      console.error('refreshCartSection failed:', err);
      // Fallback: just update the progress bar
      try {
        const cart = await fetch('/cart.js').then(r => r.json());
        this.updateProgressBarWithTotal(cart.total_price);
      } catch (_) {}
    }
  }

  /**
   * Remove item from cart
   */
  setupRemoveButtons() {
    document.querySelectorAll('.cart-item-remove').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        const line = e.target.closest('.cart-item-remove').dataset.line;
        await this.updateQuantity(line, 0);
      });
    });
  }

  /**
   * Free Minis Carousel Navigation with Progress Bar
   */
  setupFreeMinisCarousel() {
    // Setup all carousels with progress bars
    this.setupCarouselWithProgress('.free-minis-carousel', '.free-minis-track');
    this.setupCarouselWithProgress('.recommendations-carousel', '.recommendations-track');
  }

  setupCarouselWithProgress(carouselSelector, trackSelector) {
    const carousel = document.querySelector(carouselSelector);
    if (!carousel) return;

    const track = carousel.querySelector(trackSelector);
    const progressBar = carousel.querySelector('.carousel-progress-bar');
    
    if (!track || !progressBar) return;

    // Update progress bar on scroll
    const updateProgress = () => {
      const maxScroll = track.scrollWidth - track.clientWidth;
      const scrollPercentage = maxScroll > 0 ? (track.scrollLeft / maxScroll) * 100 : 0;
      progressBar.style.setProperty('--scroll-progress', `${Math.min(100, scrollPercentage)}%`);
    };

    track.addEventListener('scroll', updateProgress);
    updateProgress(); // Initial state

    // Make progress bar draggable
    let isDragging = false;

    const handleProgressClick = (e) => {
      const rect = progressBar.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const percentage = clickX / rect.width;
      const maxScroll = track.scrollWidth - track.clientWidth;
      track.scrollLeft = percentage * maxScroll;
    };

    const handleProgressDrag = (e) => {
      if (!isDragging) return;
      e.preventDefault();
      const rect = progressBar.getBoundingClientRect();
      const dragX = e.clientX - rect.left;
      const percentage = Math.max(0, Math.min(1, dragX / rect.width));
      const maxScroll = track.scrollWidth - track.clientWidth;
      track.scrollLeft = percentage * maxScroll;
    };

    progressBar.addEventListener('mousedown', (e) => {
      isDragging = true;
      handleProgressClick(e);
      progressBar.style.cursor = 'grabbing';
    });

    document.addEventListener('mousemove', handleProgressDrag);

    document.addEventListener('mouseup', () => {
      if (isDragging) {
        isDragging = false;
        progressBar.style.cursor = 'pointer';
      }
    });

    progressBar.addEventListener('click', handleProgressClick);
    progressBar.style.cursor = 'pointer';

    // Touch support for mobile
    let touchStartX = 0;
    
    progressBar.addEventListener('touchstart', (e) => {
      touchStartX = e.touches[0].clientX;
      const rect = progressBar.getBoundingClientRect();
      const touchX = touchStartX - rect.left;
      const percentage = touchX / rect.width;
      const maxScroll = track.scrollWidth - track.clientWidth;
      track.scrollLeft = percentage * maxScroll;
    });

    progressBar.addEventListener('touchmove', (e) => {
      e.preventDefault();
      const rect = progressBar.getBoundingClientRect();
      const touchX = e.touches[0].clientX - rect.left;
      const percentage = Math.max(0, Math.min(1, touchX / rect.width));
      const maxScroll = track.scrollWidth - track.clientWidth;
      track.scrollLeft = percentage * maxScroll;
    });
  }

  /**
   * Claim Free Mini Products
   */
  setupClaimButtons() {
    document.querySelectorAll('.mini-claim-btn').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        const productId = e.target.dataset.productId;
        await this.claimFreeMini(productId, e.target);
      });
    });
  }

  async claimFreeMini(variantId, button) {
    try {
      button.disabled = true;
      button.textContent = 'Adding...';

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 1,
          properties: {
            '_free_mini': 'true'
          }
        })
      });

      if (response.ok) {
        button.textContent = 'CLAIMED!';
        button.classList.add('claimed');
        
        // Get updated cart
        const cart = await fetch('/cart.js').then(r => r.json());
        
        // Update subtotal
        const subtotalElement = document.querySelector('.cart-subtotal-amount');
        if (subtotalElement) {
          subtotalElement.textContent = this.formatMoney(cart.total_price);
        }
        
        // Update progress bar immediately
        this.updateProgressBarWithTotal(cart.total_price);
        
        // Refresh cart items to show new product
        setTimeout(() => {
          this.refreshCart(cart);
        }, 500);
      }
    } catch (error) {
      console.error('Error claiming mini:', error);
      button.disabled = false;
      button.textContent = 'CLAIM';
    }
  }


  /**
   * Update Progress Bar based on cart total
   */
  updateProgressBar() {
    const progressWrapper = document.querySelector('.cart-progress-bar-wrapper');
    if (!progressWrapper) return;

    // Get current cart total from the subtotal display
    const subtotalElement = document.querySelector('.subtotal-final');
    let cartTotal = 0;

    if (subtotalElement) {
      // Extract paise from price string (e.g., "₹1,234.00" → 123400)
      const priceText = subtotalElement.textContent.replace(/[^\d.]/g, '');
      cartTotal = Math.round(parseFloat(priceText) * 100) || 0;
    } else {
      // Fallback to data attribute set during last update
      cartTotal = parseInt(progressWrapper.dataset.cartTotal) || 0;
    }

    const milestone1 = parseInt(progressWrapper.dataset['milestone-1']) || 49900;

    let progressPercent = 0;
    let milestoneReached = 0;

    if (cartTotal >= milestone1) {
      progressPercent = 100;
      milestoneReached = 1;
    } else {
      progressPercent = (cartTotal / milestone1) * 100;
    }

    // Update progress fill with smooth animation
    const progressFill = document.querySelector('.progress-fill');
    if (progressFill) {
      progressFill.style.width = `${progressPercent}%`;
    }

    // Update segments with animation
    document.querySelectorAll('.progress-segment').forEach((segment, index) => {
      if (index < milestoneReached) {
        if (!segment.classList.contains('filled')) {
          segment.classList.add('filled');
          segment.style.animation = 'segment-unlock 0.5s ease';
        }
      } else {
        segment.classList.remove('filled');
      }
    });

    // Update milestone labels
    document.querySelectorAll('.milestone-label').forEach((label, index) => {
      if (index < milestoneReached) {
        label.classList.add('reached');
      } else {
        label.classList.remove('reached');
      }
    });

    // Show/hide congrats message
    const congratsMsg = document.querySelector('.progress-congrats-message');
    if (congratsMsg) {
      if (milestoneReached >= 3) {
        congratsMsg.style.display = 'block';
        congratsMsg.style.animation = 'fadeIn 0.5s ease';
      } else {
        congratsMsg.style.display = 'none';
      }
    }

    // Update data attribute for next check
    progressWrapper.dataset.cartTotal = cartTotal;
  }

  /**
   * Refresh cart display
   */
  async refreshCart(cart) {
    // Reload the cart drawer section
    try {
      const response = await fetch(window.location.href, {
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });
      
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      const newCartContent = doc.querySelector('.cart-drawer');
      const currentCartContent = document.querySelector('.cart-drawer');
      
      if (newCartContent && currentCartContent) {
        currentCartContent.innerHTML = newCartContent.innerHTML;
        this.init(); // Reinitialize event listeners
        
        // Update progress bar after cart refresh
        setTimeout(() => {
          this.updateProgressBar();
        }, 100);
      }
    } catch (error) {
      console.error('Error refreshing cart:', error);
      // Fallback: reload the page
      window.location.reload();
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new EnhancedCartDrawer();
  });
} else {
  new EnhancedCartDrawer();
}

// Add CSS animations
const style = document.createElement('style');
style.textContent = `
  @keyframes milestone-pulse {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.1); }
  }
  
  @keyframes reward-unlock {
    0% { transform: scale(0.9); opacity: 0.5; }
    50% { transform: scale(1.05); }
    100% { transform: scale(1); opacity: 1; }
  }
  
  @keyframes segment-unlock {
    0% { transform: scaleX(0.95); }
    50% { transform: scaleX(1.02); }
    100% { transform: scaleX(1); }
  }
  
  @keyframes fadeIn {
    0% { opacity: 0; transform: translateY(-10px); }
    100% { opacity: 1; transform: translateY(0); }
  }
  
  @keyframes fadeOut {
    0% { opacity: 1; transform: translateX(0); }
    100% { opacity: 0; transform: translateX(-20px); }
  }
`;
document.head.appendChild(style);
